import { useCallback, useEffect, useRef } from 'react';

interface PressHandlers<T> {
  onLongPress: (e: React.MouseEvent<T> | React.TouchEvent<T>) => void;
  onClick?: (e: React.MouseEvent<T> | React.TouchEvent<T>) => void;
}

interface Options {
  delay?: number;
}

/**
 * Long-press with CLICK SUPPRESSION. The previous implementation fired
 * onLongPress and then let the native click (which browsers always dispatch
 * after mouseup/touchend) run the element's own onClick - so the drawer's
 * long-press-to-Support easter egg navigated to Support and was immediately
 * overridden by the Help navigation. It also declared a shouldPreventDefault
 * option it never used.
 *
 * Contract now: spread ALL returned handlers (including onClick) onto the
 * element and pass the short-press action as the hook's own onClick - the
 * element must NOT carry a separate onClick, or a long press cannot
 * suppress it.
 */
export default function useLongPress<T>(
  { onLongPress, onClick }: PressHandlers<T>,
  { delay = 300 }: Options = {},
) {
  const timeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const longPressFired = useRef(false);

  useEffect(() => {
    return () => {
      if (timeout.current) clearTimeout(timeout.current);
      timeout.current = null;
    };
  }, []);

  const start = useCallback(
    (e: React.MouseEvent<T> | React.TouchEvent<T>) => {
      longPressFired.current = false;
      timeout.current = setTimeout(() => {
        longPressFired.current = true;
        onLongPress(e);
      }, delay);
    },
    [onLongPress, delay],
  );

  const cancel = useCallback(() => {
    if (timeout.current) clearTimeout(timeout.current);
    timeout.current = null;
  }, []);

  const handleClick = useCallback(
    (e: React.MouseEvent<T>) => {
      if (longPressFired.current) {
        // The long press already acted - swallow the trailing native click.
        longPressFired.current = false;
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      onClick?.(e);
    },
    [onClick],
  );

  return {
    onMouseDown: (e: React.MouseEvent<T>) => start(e),
    onTouchStart: (e: React.TouchEvent<T>) => start(e),
    onMouseUp: () => cancel(),
    onMouseLeave: () => cancel(),
    onTouchEnd: () => cancel(),
    onClick: handleClick,
  };
}
